import { i18n } from '@/plugins/vueI18n.js'
import appSettings from '@/plugins/appSettings'
import formatter from '@/plugins/formatter'

const accountTypeMap = {
  SVGS: i18n.t('termDepositActions.listAC.savingsLabel'),
  CACC: i18n.t('termDepositActions.listAC.currentLabel'),
  TXSV: i18n.t('termDepositActions.listAC.cashIsaLabel'),
  TXEA: i18n.t('termDepositActions.listAC.txeaCashIsaLabel'),
  TXFR: i18n.t('termDepositActions.listAC.txfrCashIsaLabel'),
  TXNA: i18n.t('termDepositActions.listAC.taxNoticeLabel'),
  TXLA: i18n.t('termDepositActions.listAC.lisaCashIsaLabel'),
  NACC: i18n.t('termDepositActions.listAC.noticeLabel')
}

const balanceLabel = i18n.t('common.balLabel')

export const getAccountTypeLabel = (accountType) => {
  return accountTypeMap[accountType] ?? ''
}

export const formatPrimaryLine = (account) => renderAccountTemplate('accountDropdownPrimaryLine', account)
export const formatSecondaryLine = (account) => renderAccountTemplate('accountDropdownSecondaryLine', account)
export const formatSelectedAccount = (account) => renderAccountTemplate('selectedAccount', account)
export const formatSelectedHint = (account) => renderAccountTemplate('selectedAccountHint', account)

/**
 * Renders an account template with the provided account data.
 *
 * @param {string} templateKey - The key to identify the template in the configuration.
 * @param {Object} account - The account object containing various account details.
 * @param {string} account.accountType - The type of the account.
 * @param {string} account.accountId - The ID of the account.
 * @param {string} account.iban - The IBAN of the account.
 * @param {number} account.balance - The balance of the account.
 * @param {string} account.currency - The currency of the account balance.
 * @param {string} account.accountName - The name of the account.
 * @param {string} account.accountDescription - The description of the account.
 * @returns {string} - The rendered template with placeholders replaced by account data.
 *
 * @example
 * const account = {
 *   accountType: 'savings',
 *   accountId: '123456789',
 *   iban: 'DE89370400440532013000',
 *   balance: 1000,
 *   currency: 'USD',
 *   accountName: 'John Doe Savings',
 *   accountDescription: 'Primary savings account'
 * };
 * const result = renderAccountTemplate('accountItemLine1', account);
 * console.log(result); // Output will depend on the template configuration
 */

export const renderAccountTemplate = (templateKey, account) => {
  const { accountType, accountId, iban, balance, currency, accountName, accountDescription } = account
  const template = appSettings.uiFieldsDisplayConfigurations.template[templateKey]

  if (!template) {
    console.error(`Template for key ${templateKey} not found.`)
    return ''
  }

  const data = {
    accountType: getAccountTypeLabel(accountType),
    accountNumber: formatter.formatAccount(accountId, iban),
    amount: formatter.currency.format(balance, currency),
    balanceLabel,
    accountName,
    accountDescription
  }

  return replacePlaceholder(template, data)
}

/**
 * Replaces placeholders in a template string with corresponding values from a data object.
 *
 * @param {string} template - The template string containing placeholders in the format {key}.
 * @param {Object} data - An object containing key-value pairs where the key corresponds to the placeholder name in the template.
 * @returns {string} - The template string with placeholders replaced by corresponding values from the data object.
 *
 * @example
 * const template = "Hello, { name }! Your balance is { balance } { currency }.";
 * const data = {
 *   name: "John Doe",
 *   balance: "1000",
 *   currency: "USD"
 * };
 * const result = replacePlaceholder(template, data);
 * console.log(result); // Output: "Hello, John Doe! Your balance is 1000 USD."
 */

export const replacePlaceholder = (template, data) => {
  return template.replace(/{(.*?)}/g, (_, key) => {
    const trimmedKey = key.trim()
    return trimmedKey in data ? data[trimmedKey] : `{${trimmedKey}}`
  })
}
